import React, { useState } from 'react';

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';

import Home from '../Components/Home/Home';
import Login from '../Components/Authentication/Login';
import PasswordReset from '../Components/Authentication/components/PasswordReset';
import Dashboard from '../Components/Dashboard/Dashboard';
import Dependencies from '../Components/Dependencies/Dependencies';
import DependencyDeleted from '../Components/Dependencies/components/DependencyDeleted/DependencyDeleted';
import DependenciesDetail from '../Components/Dependencies/DependenciesDetail';
import Controls from '../Components/Controls/Controls';
import ControlsDeleted from '../Components/Controls/components/ControlDeleted/ControlsDeleted';
import ControlsDetail from '../Components/Controls/ControlsDetail';
import Assets from '../Components/Assets/Assets';
import AssetsDeleted from '../Components/Assets/components/AssetsDeleted/AssetsDeleted';
import AssetsDetail from '../Components/Assets/AssetsDetail';
import Assignments from '../Components/Assignment/Assignments';
import Evaluation from '../Components/Evaluation/Evaluation';
import Gaps from '../Components/Gaps/Gaps';
import GapsDetail from '../Components/Gaps/GapsDetail';
import Tasks from '../Components/Tasks/Tasks';
import TasksDetail from '../Components/Tasks/TasksDetail';
import Settings from '../Components/Settings/Settings';
import Navbar from '../Components/Navbar/Navbar';
import UserProfile from '../Components/UserProfile/UserProfile';
import Security from '../Components/UserManagement/UserManagement';
import RoleView from '../Components/UserManagement/components/RoleView/RoleView';
import NotFound from '../Components/Common/NotFound/NotFound';
import NotFoundPublic from '../Components/Common/NotFound/NotFoundPublic';

import PrivateRoute from './PrivateRoutes'
import GapsDeleted from '../Components/Gaps/components/GAPsDeleted/GAPsDeleted';
import IssuesDeleted from '../Components/Gaps/components/IssuesDeleted/IssuesDeleted';
import UsersDeleted from '../Components/UserManagement/components/UsersDeleted/UsersDeleted';
import ActivateAccount from '../Components/Authentication/components/ActivateAccount';
import RolesDeleted from '../Components/UserManagement/components/RoleDeleted/RolesDeleted';
import Licenses from '../Components/Licences/Licences';
import TasksDeleted from '../Components/Tasks/components/TasksDeleted/TasksDeleted';
import EvaluationDeleted from '../Components/Evaluation/components/EvaluationDeleted/EvaluationDeleted';
import Processes from '../Components/Processes/Processes';
import WorkflowEditor from '../Components/Processes/WorkflowEditor';

const { Header, Content, Footer } = Layout;

const AppRoutes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Estado de autenticación
  const [collapsed, setCollapsed] = useState(true); // Estado de colapso de la barra lateral

  const handleLogin = () => {
    setIsAuthenticated(true); // Cambiar estado cuando el login es exitoso
  };

  const handleLogout = () => {
    setIsAuthenticated(false); // Cambiar estado al hacer logout
  };

  // Componente de Layout principal que contiene el Navbar
  const MainLayout = ({ children }) => {
    return (
      <Layout style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header>
          <Navbar onLogout={handleLogout} collapsed={collapsed} setCollapsed={setCollapsed} />
        </Header>
  
        <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: "margin-left 0.3s", flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Aseguramos que el contenido no crezca más allá del espacio disponible */}
          <Content style={{ padding: "20px", flexGrow: 1, overflow: "auto" }}>
            {children}
          </Content>
  
          {/* Footer siempre abajo */}
          <Footer style={{ textAlign: "right", padding: "10px 20px", marginTop: 20 }}>
            <img
              src="/poweredby-exa.svg"
              alt="Marca de agua"
              style={{
                width: 200,
                opacity: 0.7,
              }}
            />
          </Footer>
        </Layout>
      </Layout>
    );
  };
  
  return (
    <Router>
      {/* Rutas principales */}
      <Routes>
        {/* Ruta para Login, no debe tener Navbar */}
        <Route path="/login" element={<Login onLogin={handleLogin} />} />

        {/* Ruta para reset desde email con token */}
        <Route path="/reset-password/:uid/:token" element={<PasswordReset />} />
        
        {/* Ruta para reset desde login (force_change/is_expired) */}
        <Route path="/reset-password" element={<PasswordReset />} />

        {/* Ruta para reset desde login (force_change/is_expired) */}
        <Route path="/activate-account/:uid/:token" element={<ActivateAccount />} />

        {/* Redirección predeterminada si no está autenticado */}
        <Route path="/" element={<Navigate to="/login" />} />

        {/* Rutas protegidas, que requieren estar autenticado */}
        <Route
          path="/home"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Home />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Dashboard />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/assets"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Assets />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/assets-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <AssetsDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/assets/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <AssetsDetail />
              </MainLayout>
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dependencies"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Dependencies />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/dependencies-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <DependencyDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/dependencies/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <DependenciesDetail />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/controls"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Controls />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/controls-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <ControlsDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/controls/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <ControlsDetail />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/assignment"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Assignments />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/evaluation"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Evaluation />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/evaluation-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <EvaluationDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/gaps"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Gaps />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/gaps-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <GapsDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/gaps/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <GapsDetail />
              </MainLayout>
            </PrivateRoute>
          }
        />

        {/* <Route
          path="/issues-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <IssuesDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        /> */}

        <Route
          path="/tasks"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Tasks />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/tasks-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <TasksDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/tasks/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <TasksDetail />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/processes"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Processes />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/workflows/:code"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <WorkflowEditor />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Settings />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/userprofile"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <UserProfile />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/user-management"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Security />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/users-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <UsersDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/roles-deleted"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <RolesDeleted />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/user-management/roles/create"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <RoleView />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/user-management/roles/:id"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <RoleView />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/licences"
          element={
            <PrivateRoute>
              <MainLayout collapsed={collapsed}>
                <Licenses />
              </MainLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="*"
          element={
            localStorage.getItem("access_token") ? (
              <MainLayout collapsed={collapsed}>
                <NotFound />
              </MainLayout>
            ) : (
              <NotFoundPublic />
            )
          }
        />

      </Routes>
    </Router>
  );
};

export default AppRoutes;
